import { useQuery, useMutation } from "@apollo/client";
import {
  GET_SELLER_STORE,
  UPDATE_STORE_CLOSING_HOURS,
} from "../../api/seller-store";
import clsx from "clsx";
import { useState, useRef, useEffect } from "react";
import LoadingSpinnerInline from "../../../../components/common/loading-spinner/loading-spinner-inline";
import { HiChevronRight } from "react-icons/hi2";
import { useNavigate } from "react-router-dom";

const HeaderLiveSales = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedHour, setSelectedHour] = useState("00");
  const [selectedMinute, setSelectedMinute] = useState("00");
  const [isVisible, setIsVisible] = useState(false);
  const { data: storeData, refetch } = useQuery(GET_SELLER_STORE, {
    fetchPolicy: "cache-and-network",
  });
  const [updateStoreClosingHours, { loading: updateLoading }] = useMutation(
    UPDATE_STORE_CLOSING_HOURS
  );
  const hourScrollRef = useRef<HTMLDivElement>(null);
  const minuteScrollRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (storeData) {
      const hasProducts =
        storeData?.getSellerStore?.todaysProducts &&
        storeData?.getSellerStore?.todaysProducts.length !== 0;

      setIsVisible(hasProducts);
    }
  }, [storeData]);

  useEffect(() => {
    const initializeScroll = async () => {
      if (isModalOpen && storeData?.getSellerStore?.closingHours) {
        const [hour, minute] = storeData.getSellerStore.closingHours.split(":");
        setSelectedHour(hour);
        setSelectedMinute(minute);

        // DOM 업데이트를 기다림
        await new Promise((resolve) => setTimeout(resolve, 0));

        if (hourScrollRef.current && minuteScrollRef.current) {
          const hourIndex = parseInt(hour);
          const minuteIndex = Math.floor(parseInt(minute) / 5);
          const itemHeight = 40;

          hourScrollRef.current.scrollTop = hourIndex * itemHeight;
          minuteScrollRef.current.scrollTop = minuteIndex * itemHeight;
        }
      }
    };

    initializeScroll();
  }, [isModalOpen, storeData]);

  // 모달 열릴 사 다른 화면 스크롤 방지
  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isModalOpen]);

  const handleCompleteModal = async () => {
    if (
      selectedHour === storeData?.getSellerStore?.closingHours.split(":")[0] &&
      selectedMinute === storeData?.getSellerStore?.closingHours.split(":")[1]
    ) {
      alert("변경사항이 없어요.");
      setIsModalOpen(false);
      return;
    }

    const isConfirmed = window.confirm(
      `현재 판매 중인 실시간 팩에 모두 적용돼요!\n마감시간을 [${storeData?.getSellerStore?.closingHours}]에서 [${selectedHour}:${selectedMinute}]로 변경할까요?`
    );
    if (isConfirmed) {
      await updateStoreClosingHours({
        variables: {
          closingHours: `${selectedHour}:${selectedMinute}`,
        },
      });
      refetch();
      setIsModalOpen(false);
    }
  };

  return (
    <>
      <div className="fixed top-0 left-0 right-0 z-50 max-w-[742px] mx-auto flex items-center justify-between px-4 w-full bg-white border-b border-gray-200 h-[60px]">
        <div className="flex flex-col">
          <div className="flex items-center gap-2">
            <div
              className={clsx(
                "w-4 h-4 rounded-full transition-colors shadow-sm",
                isVisible
                  ? "bg-blue-500 ring-2 ring-blue-200"
                  : "bg-gray-500 ring-2 ring-gray-200"
              )}
            ></div>
            <div className="font-semibold text-md">
              {isVisible ? "실시간 판매 진행중" : "실시간 판매 대기중"}
            </div>
          </div>
          <div
            onClick={() => {
              navigate(
                `/boss/store-preview/card/${storeData?.getSellerStore?.id}`
              );
            }}
            className="ml-6 font-semibold underline underline-offset-2 text-gray-500 text-sm flex items-center"
          >
            내 매장 노출 보기 <HiChevronRight className="w-3.5 h-3.5" />
          </div>
        </div>
        <div className="flex items-center">
          <div className="text-gray-800 font-medium bg-gray-100 px-3 py-1.5 rounded-l-full border-r border-gray-200 w-28 text-end">
            <span
              className={clsx(
                "text-gray-900 font-bold transition-all duration-200",
                storeData?.getSellerStore?.closingHours
                  ? "opacity-100"
                  : "opacity-0"
              )}
            >
              {storeData?.getSellerStore?.closingHours}
            </span>{" "}
            <span className="text-gray-600">까지</span>
          </div>
          <button
            onClick={() => setIsModalOpen(true)}
            className="px-3 py-1.5 bg-blue-500 text-white rounded-r-full shadow-sm active:brightness-90 font-bold"
          >
            변경
          </button>
        </div>
      </div>

      {isModalOpen && (
        <div className="z-40 fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
          <div className="bg-white p-6 rounded-lg w-[320px]">
            <h2 className="text-xl font-semibold mb-6">
              실시간 판매 마감시간 설정
            </h2>

            <div className="flex justify-center py-4 space-x-6">
              {/* 시간 선택 */}
              <div
                ref={hourScrollRef}
                className="overflow-y-auto max-h-60 w-24 text-center"
              >
                {Array.from({ length: 24 }, (_, hour) => {
                  const hourStr = String(hour).padStart(2, "0");
                  return (
                    <button
                      key={hourStr}
                      className={clsx(
                        "w-full py-2 text-lg",
                        selectedHour === hourStr
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100"
                      )}
                      onClick={() => setSelectedHour(hourStr)}
                    >
                      {hourStr}
                    </button>
                  );
                })}
              </div>
              <span className="text-lg font-bold pt-2">:</span>
              {/* 분 선택 */}
              <div
                ref={minuteScrollRef}
                className="overflow-y-auto max-h-60 w-24 text-center"
              >
                {Array.from({ length: 12 }, (_, i) => {
                  const minute = String(i * 5).padStart(2, "0");
                  return (
                    <button
                      key={minute}
                      className={clsx(
                        "w-full py-2 text-lg",
                        selectedMinute === minute
                          ? "bg-blue-500 text-white"
                          : "bg-gray-100"
                      )}
                      onClick={() => setSelectedMinute(minute)}
                    >
                      {minute}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => setIsModalOpen(false)}
                className="px-4 py-2 text-gray-600 bg-gray-100 active:bg-gray-200 rounded-lg"
              >
                취소
              </button>
              <button
                onClick={handleCompleteModal}
                className={clsx(
                  "px-4 py-2 bg-blue-500 text-white rounded-lg active:bg-blue-600",
                  updateLoading && "opacity-50"
                )}
                disabled={updateLoading}
              >
                {updateLoading ? <LoadingSpinnerInline /> : "저장"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default HeaderLiveSales;
